import { useCallback } from 'react';
import { plural } from '@lingui/core/macro';
import { useLingui } from '@lingui/react/macro';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { addChampionshipTeams } from 'modules/championship/store/championship/actions';
import { Team } from 'modules/teams/domain/interfaces/team';
import { getChampionshipTeamsV2 } from 'modules/teams/store/championship-teams/actions';
import { TeamPickerModal } from 'modules/teams/ui/components/team-picker-modal';
import { trackEvent } from 'shared/analytics';
import { useAppDispatch } from 'store/hooks';

interface AddTeamsModalProps {
  championshipId: number;
  /** Команды, уже заявленные в этот турнир: в выдаче они видны, но выбрать их нельзя. */
  existTeamsId: number[];
  onClose: () => void;
}

/**
 * Добавление команд в заявки, второй флоу (v2).
 *
 * Выбор команд — общий `TeamPickerModal` (выдача сразу при открытии, уже заявленная
 * команда отключена с пометкой); здесь остаётся только то, что относится к соревнованию.
 * Отличие от формы старого флоу (`add-team-application-form`), кроме этого, одно: команд
 * можно выбрать несколько за раз.
 *
 * Добавление — одна мутация на все выбранные команды (`addChampionshipTeams`), поэтому
 * частичного успеха не бывает: либо добавились все, либо ни одной и окно остаётся открытым
 * с прежним выбором. Список после мутации перечитывается с сервера — локальной дорисовки
 * состава, как в v1, здесь нет.
 */
const AddTeamsModal = ({ championshipId, existTeamsId, onClose }: AddTeamsModalProps) => {
  const { t } = useLingui();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const handleSubmit = useCallback(
    async (teams: Team[]) => {
      const chosen = teams.map((team) => team.id);

      try {
        await dispatch(addChampionshipTeams({ teamIds: chosen, championshipId })).unwrap();
        // Список v2 читается с сервера (`teams` + `championshipApplications`), локальной
        // дорисовки в редьюсере у него нет — без перечитывания новых команд на экране не будет.
        await dispatch(getChampionshipTeamsV2({ championshipId }));
      } catch {
        toast.error(t`Не удалось добавить команды — попробуйте ещё раз`);
        return;
      }

      // Мутация одна на все выбранные команды и частичного успеха не бывает, поэтому
      // событие одно, а сколько команд добавили — в `teams_count`.
      trackEvent('Team Added To Championship', { championship_id: championshipId, flow: 'v2', teams_count: chosen.length });

      const count = chosen.length;
      toast.success(t`${plural(count, { one: 'Команда добавлена в турнир', other: 'Добавлено команд: #' })}`);
      onClose();
      // Одну команду добавляют, чтобы тут же заявить её игроков — ведём внутрь.
      if (chosen.length === 1) navigate(String(chosen[0]));
    },
    [dispatch, championshipId, t, onClose, navigate],
  );

  return (
    <TeamPickerModal
      isMulti
      title={t`Добавить команды`}
      subtitle={t`Команды берутся из вашей организации — выбрать можно сразу несколько.`}
      submitLabel={t`Добавить`}
      disabledTeamIds={existTeamsId}
      disabledHint={t`уже в турнире`}
      onSubmit={handleSubmit}
      onClose={onClose}
    />
  );
};

export default AddTeamsModal;
